import { useLocale } from '@/shared/lib/i18n/locale-context';

/**
 * The shape of a table while its rows are still arriving.
 *
 * ⚠️ **Replaces a line of grey text that said "loading".** The text occupied one line, so the page
 * assembled itself at whatever height the data turned out to be — the header jumped, the filters
 * jumped, and anything an operator had already reached for moved out from under the pointer. On a
 * screen read standing up, a control that moves as you reach for it is worse than a slow one.
 *
 * ⚠️ **Announced, not just drawn.** `aria-busy` with a live region tells a screen-reader user that
 * something is coming; bars that pulse tell them nothing at all.
 *
 * ⚠️ Respects `prefers-reduced-motion` through Tailwind's `motion-safe:` — a pulsing block is
 * exactly the kind of animation that costs some readers more than it gives them.
 */
export function TableSkeleton({ rows = 5, columns = 5 }: { rows?: number; columns?: number }) {
  const { t } = useLocale();

  return (
    <div aria-busy="true" aria-live="polite" className="flex flex-col">
      <span className="sr-only">{t.app.loading}</span>

      {Array.from({ length: rows }).map((_, row) => (
        <div
          key={row}
          className="flex items-center gap-4 border-b border-gray-100 px-5 py-4 last:border-0"
        >
          {Array.from({ length: columns }).map((_, column) => (
            <div
              key={column}
              className="motion-safe:animate-pulse h-3 rounded bg-gray-100"
              style={{
                // Uneven widths, because a grid of identical bars reads as a rendering fault rather
                // than as content on its way.
                flex: column === 0 ? 2 : 1,
                opacity: 1 - row * 0.12,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
